import type { CollateralKey } from "@/data/fixtures";

/** Single assets (contains) or exact multiasset combos. */
export type HuntTargetUi =
	| { kind: "asset"; asset: CollateralKey }
	| { kind: "combo"; combo: "IcpBtc" | "EthBtc" | "IcpBtcEth" };

const COMBO_SETS: Record<
	"IcpBtc" | "EthBtc" | "IcpBtcEth",
	ReadonlySet<CollateralKey>
> = {
	IcpBtc: new Set(["ICP", "ckBTC"]),
	EthBtc: new Set(["ckETH", "ckBTC"]),
	IcpBtcEth: new Set(["ICP", "ckBTC", "ckETH"]),
};

export function vaultAssetSet(
	lines: Array<{ asset: CollateralKey; amount: number }>,
): Set<CollateralKey> {
	const s = new Set<CollateralKey>();
	for (const line of lines) {
		if (line.amount > 0) s.add(line.asset);
	}
	return s;
}

function setsEqual(a: Set<CollateralKey>, b: ReadonlySet<CollateralKey>): boolean {
	if (a.size !== b.size) return false;
	for (const x of a) {
		if (!b.has(x)) return false;
	}
	return true;
}

/** OR match: contains-asset for singles; exact set for combos. */
export function vaultMatchesHunt(
	targets: HuntTargetUi[],
	lines: Array<{ asset: CollateralKey; amount: number }>,
): boolean {
	if (targets.length === 0) return true;
	const held = vaultAssetSet(lines);
	if (held.size === 0) return false;
	for (const t of targets) {
		if (t.kind === "asset") {
			if (held.has(t.asset)) return true;
		} else if (setsEqual(held, COMBO_SETS[t.combo])) {
			return true;
		}
	}
	return false;
}

/** Match gateway defaultHuntTargets: all singles + all combos. */
export function defaultHuntTargets(): HuntTargetUi[] {
	return [
		{ kind: "asset", asset: "ICP" },
		{ kind: "asset", asset: "ckBTC" },
		{ kind: "asset", asset: "ckETH" },
		{ kind: "combo", combo: "IcpBtc" },
		{ kind: "combo", combo: "EthBtc" },
		{ kind: "combo", combo: "IcpBtcEth" },
	];
}

/** Protocol HF ≡ CR. e8 units: 1.00× = 100_000_000. */
export const HUNT_HF_FLOOR = 1.0;
export const HUNT_HF_CAP = 1.3;
export const HUNT_HF_E8 = 100_000_000;

export function hfToE8(hf: number): bigint {
	return BigInt(Math.round(hf * HUNT_HF_E8));
}

export function e8ToHf(e8: bigint | number): number {
	return Number(e8) / HUNT_HF_E8;
}

/** Inclusive CR band check for hunt / liquidate filters. */
export function vaultMatchesHfRange(
	cr: number,
	minE8: bigint | number,
	maxE8: bigint | number,
): boolean {
	if (!Number.isFinite(cr) || cr <= 0) return false;
	const min = e8ToHf(minE8);
	const max = e8ToHf(maxE8);
	return cr + 1e-12 >= min && cr - 1e-12 <= max;
}
